import { ApiError, reliableRequest } from './reliableRequest';
import type {
  AdminCandidateDetail,
  AdminCandidateListResponse,
  AdminSession,
  Candidate,
  CandidatePipeline,
  CreateCandidatePayload,
  InviteVerifyResponse,
  IdentityInquiryResponse,
  IdentityStatusResponse,
  ResumeUploadResponse,
  ParsedResume,
  InterviewSession,
  LiveKitTokenResponse,
  ClientTranscriptMessage,
  DimensionScore,
  CompositeScore,
  HireResponse,
} from '../types';

/** Combined response from the start-and-join endpoint. */
export interface InterviewStartWithTokenResponse extends InterviewSession {
  token: string;
  room_name: string;
  livekit_url: string;
}

// ─── Base URL ─────────────────────────────────────────────────────────────────

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

// ─── Session token ────────────────────────────────────────────────────────────

const SESSION_TOKEN_KEY = 'baba_session_token';
const CANDIDATE_ID_KEY = 'baba_candidate_id';
const ADMIN_SESSION_TOKEN_KEY = 'baba_admin_session_token';

// Initialize from sessionStorage on module load (handles page refreshes)
let _sessionToken: string | null = sessionStorage.getItem(SESSION_TOKEN_KEY);
let _adminSessionToken: string | null = sessionStorage.getItem(ADMIN_SESSION_TOKEN_KEY);

export function setSessionToken(token: string, candidateId: string): void {
  _sessionToken = token;
  sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  sessionStorage.setItem(CANDIDATE_ID_KEY, candidateId);
}

export function clearSessionToken(): void {
  _sessionToken = null;
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(CANDIDATE_ID_KEY);
}

export function getStoredCandidateId(): string | null {
  return sessionStorage.getItem(CANDIDATE_ID_KEY);
}

function setAdminSessionToken(token: string): void {
  _adminSessionToken = token;
  sessionStorage.setItem(ADMIN_SESSION_TOKEN_KEY, token);
}

export function clearAdminSessionToken(): void {
  _adminSessionToken = null;
  sessionStorage.removeItem(ADMIN_SESSION_TOKEN_KEY);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Structured error thrown when an API request receives a non-2xx response.
 * Includes the HTTP status code and, when available, the parsed error body.
 */
export type InviteErrorKind = 'expired' | 'invalid' | 'temporary';

export function classifyInviteError(error: unknown): InviteErrorKind {
  if (!(error instanceof ApiError)) {
    return 'temporary';
  }
  if (error.status === 410) {
    return 'expired';
  }
  if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
    return 'invalid';
  }
  return 'temporary';
}

const INVITE_RETRY_DELAYS_MS = [250, 750];

async function requestInvite<T>(operation: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      const retryDelay = INVITE_RETRY_DELAYS_MS[attempt];
      if (classifyInviteError(error) !== 'temporary' || retryDelay === undefined) {
        throw error;
      }
      await new Promise(resolve => window.setTimeout(resolve, retryDelay));
    }
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`;

  const headers = new Headers({ Accept: 'application/json' });
  if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (_sessionToken) headers.set('Authorization', `Bearer ${_sessionToken}`);
  if (_adminSessionToken) headers.set('X-Admin-Session', _adminSessionToken);
  new Headers(options.headers).forEach((value, name) => headers.set(name, value));

  return reliableRequest<T>(url, {
    credentials: 'include',
    ...options,
    headers,
  });
}

// ─── Candidate Endpoints ──────────────────────────────────────────────────────

/**
 * Create a new candidate profile for the hiring pipeline.
 * Returns the newly created candidate with a server-assigned ID and session token.
 */
export async function createCandidate(data: CreateCandidatePayload): Promise<Candidate> {
  return request<Candidate>('/api/candidates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Fetch an existing candidate by their unique ID.
 */
export async function getCandidate(id: string): Promise<Candidate> {
  return request<Candidate>(`/api/candidates/${id}`);
}

/**
 * Mint a fresh investor-demo candidate (Connor Sweeney) pre-gated through
 * Stage 1 so the session lands directly on the case-sim prep page.
 */
export async function startInvestorDemo(): Promise<Candidate & { session_token: string }> {
  return request<Candidate & { session_token: string }>('/api/demo/case-sim-session', {
    method: 'POST',
  });
}

export async function getByInviteToken(inviteToken: string): Promise<InviteVerifyResponse> {
  return requestInvite(() =>
    request<InviteVerifyResponse>(`/api/candidates/by-invite-token/${encodeURIComponent(inviteToken)}`),
  );
}

export async function resolveInviteCode(code: string): Promise<{ invite_token: string }> {
  return requestInvite(() =>
    request<{ invite_token: string }>(`/api/i/${encodeURIComponent(code)}`),
  );
}

export async function getPipelineState(candidateId: string): Promise<CandidatePipeline> {
  return requestInvite(() => request<CandidatePipeline>(`/api/candidates/${candidateId}/pipeline`));
}

/**
 * Persist the candidate's current step/page so they resume here on return.
 * Fire-and-forget — errors are non-fatal.
 */
export async function updateCurrentStep(candidateId: string, step: string): Promise<void> {
  return request<void>(`/api/candidates/${candidateId}/step`, {
    method: 'PATCH',
    body: JSON.stringify({ step }),
  });
}

export async function createIdentityInquiry(candidateId: string): Promise<IdentityInquiryResponse> {
  return request<IdentityInquiryResponse>(`/api/candidates/${candidateId}/identity/inquiry`, {
    method: 'POST',
  });
}

export async function getIdentityStatus(candidateId: string): Promise<IdentityStatusResponse> {
  return request<IdentityStatusResponse>(`/api/candidates/${candidateId}/identity`);
}

export async function resumeIdentityInquiry(candidateId: string): Promise<IdentityInquiryResponse> {
  return request<IdentityInquiryResponse>(`/api/candidates/${candidateId}/identity/resume`, {
    method: 'POST',
  });
}

// ─── Resume Endpoints ─────────────────────────────────────────────────────────

/** Load the saved resume without another upload. */
export async function getSavedResume(candidateId: string, signal?: AbortSignal): Promise<ParsedResume> {
  return request<ParsedResume>(`/api/candidates/${candidateId}/resume`, { signal });
}

export async function fetchResumeFromAshby(candidateId: string): Promise<ResumeUploadResponse> {
  return request<ResumeUploadResponse>(`/api/candidates/${candidateId}/resume/fetch-from-ashby`, {
    method: 'POST',
  });
}

export async function uploadResume(
  candidateId: string,
  file: File,
): Promise<ResumeUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  return request<ResumeUploadResponse>(`/api/candidates/${candidateId}/resume`, {
    method: 'POST',
    body: formData,
  });
}

// ─── Interview Endpoints ──────────────────────────────────────────────────────

/**
 * Start a new interview session for the given candidate and stage.
 */
export async function startInterview(
  candidateId: string,
  stage: 1 | 2,
  consentGiven: boolean = true,
): Promise<InterviewSession> {
  return request<InterviewSession>(`/api/interviews/start`, {
    method: 'POST',
    body: JSON.stringify({ candidate_id: candidateId, stage, consent_given: consentGiven }),
  });
}

/**
 * Start a new interview AND obtain a LiveKit token in a single round-trip.
 */
export async function startAndJoinInterview(
  candidateId: string,
  stage: 1 | 2,
  consentGiven: boolean = true,
): Promise<InterviewStartWithTokenResponse> {
  return request<InterviewStartWithTokenResponse>(`/api/interviews/start-and-join`, {
    method: 'POST',
    body: JSON.stringify({ candidate_id: candidateId, stage, consent_given: consentGiven }),
  });
}

/**
 * Poll the current status of an in-progress or completed interview.
 */
export async function getInterviewStatus(interviewId: string): Promise<InterviewSession> {
  return request<InterviewSession>(`/api/interviews/${interviewId}`);
}

export async function pauseInterview(interviewId: string): Promise<{ interview_id: string; status: string; message: string }> {
  return request(`/api/interviews/${interviewId}/pause`, { method: 'POST' });
}

export async function resumeInterview(interviewId: string): Promise<{ interview_id: string; status: string; message: string; elapsed_seconds?: number }> {
  return request(`/api/interviews/${interviewId}/resume`, { method: 'POST' });
}

/**
 * Request a short-lived LiveKit token so the candidate can join the voice room.
 */
export async function getLiveKitToken(
  candidateId: string,
  interviewId: string,
): Promise<LiveKitTokenResponse> {
  return request<LiveKitTokenResponse>(`/api/interviews/${interviewId}/token`, {
    method: 'POST',
    body: JSON.stringify({ candidate_id: candidateId }),
  });
}

// ─── Written Communication (Phase 5) Endpoints ─────────────────────────────

/**
 * Generate Margaret's text reply for the Phase 5 texting exercise.
 */
export async function getMargaretReply(
  interviewId: string,
  candidateId: string,
  message: string,
  conversationHistory: Array<{ role: string; content: string }>,
): Promise<import('../types').MargaretReplyResponse> {
  return request<import('../types').MargaretReplyResponse>(
    `/api/interviews/${interviewId}/margaret-reply`,
    {
      method: 'POST',
      body: JSON.stringify({
        candidate_id: candidateId,
        message,
        conversation_history: conversationHistory,
      }),
    },
  );
}

/**
 * Fetch the server-side writing draft for an interview.  Used by the standalone
 * /writing/:id page on mount to rehydrate whatever the candidate typed before
 * closing the tab.
 */
export async function getWritingDraft(
  interviewId: string,
): Promise<import('../types').WritingDraftResponse> {
  return request<import('../types').WritingDraftResponse>(
    `/api/interviews/${interviewId}/writing-draft`,
  );
}

/**
 * Autosave an in-progress writing draft to the server.  Called throttled as
 * the candidate types so cross-device / cross-browser resume works.
 */
export async function saveWritingDraft(
  interviewId: string,
  candidateId: string,
  body: {
    email_draft?: { to: string; subject: string; body: string } | null;
    text_exchange?: Array<{ role: string; content: string }>;
    paste_events?: Array<{ field: string; timestamp: number; length: number }>;
    step?: string | null;
  },
): Promise<import('../types').WritingDraftResponse> {
  return request<import('../types').WritingDraftResponse>(
    `/api/interviews/${interviewId}/writing-draft`,
    {
      method: 'PUT',
      body: JSON.stringify({ candidate_id: candidateId, ...body }),
    },
  );
}

/**
 * Submit completed written communication (email + texts) for scoring.
 */
export async function submitWrittenWork(
  interviewId: string,
  candidateId: string,
  emailDraft: { to: string; subject: string; body: string },
  textExchange: Array<{ role: string; content: string }>,
  pasteEvents?: Array<{ field: string; timestamp: number; length: number }>,
): Promise<import('../types').WrittenSubmissionResponse> {
  return request<import('../types').WrittenSubmissionResponse>(
    `/api/interviews/${interviewId}/submit-writing`,
    {
      method: 'POST',
      body: JSON.stringify({
        candidate_id: candidateId,
        email_draft: emailDraft,
        text_exchange: textExchange,
        paste_events: pasteEvents,
      }),
    },
  );
}

// ─── Tech Literacy Endpoints ──────────────────────────────────────────────────

/**
 * Fetch the tech-literacy scenario (search + calendar tasks plus any prior
 * submissions) for a stage-2 interview.  Used by /search-resources/:id,
 * /writing/:id, and /schedule-margaret/:id.
 */
export async function getTechLiteracyScenario(
  interviewId: string,
  candidateId: string,
): Promise<import('../types').TechLiteracyScenario> {
  return request<import('../types').TechLiteracyScenario>(
    `/api/tech-literacy/${interviewId}/scenario?candidate_id=${encodeURIComponent(candidateId)}`,
  );
}

/**
 * Submit the resource-search step: the URL the candidate would use to refer
 * Margaret, plus their query history and rationale.
 */
export async function submitTechLiteracySearch(
  interviewId: string,
  body: {
    candidate_id: string;
    final_query: string;
    all_queries: string[];
    selected_result_id?: string | null;
    submitted_url: string;
    notes?: string | null;
    elapsed_seconds?: number | null;
  },
): Promise<import('../types').TechLiteracySearchSubmission> {
  return request<import('../types').TechLiteracySearchSubmission>(
    `/api/tech-literacy/${interviewId}/search`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

/**
 * Submit the calendar step: the follow-up event the candidate scheduled
 * with Margaret.
 */
export async function submitTechLiteracyCalendar(
  interviewId: string,
  body: {
    candidate_id: string;
    title: string;
    start_at: string;
    end_at: string;
    invitees: string[];
    video_link?: string | null;
    notes?: string | null;
    elapsed_seconds?: number | null;
  },
): Promise<import('../types').TechLiteracyCalendarSubmission> {
  return request<import('../types').TechLiteracyCalendarSubmission>(
    `/api/tech-literacy/${interviewId}/calendar`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

// ─── Scoring Endpoints ────────────────────────────────────────────────────────

/**
 * Retrieve per-dimension scores for a candidate across all completed stages.
 */
export async function getCandidateScores(candidateId: string): Promise<DimensionScore[]> {
  return request<DimensionScore[]>(`/api/candidates/${candidateId}/scores`);
}

/**
 * Retrieve composite readiness scores for a candidate.
 */
export async function getCandidateReadiness(candidateId: string): Promise<CompositeScore[]> {
  return request<CompositeScore[]>(`/api/candidates/${candidateId}/readiness`);
}

// ─── Admin Dashboard Endpoints ──────────────────────────────────────────────

export async function adminLogin(username: string, password: string): Promise<AdminSession> {
  clearAdminSessionToken();
  const session = await request<AdminSession>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  if (session.session_token) {
    setAdminSessionToken(session.session_token);
  }
  return session;
}

export async function adminLogout(): Promise<void> {
  await request<void>('/api/admin/logout', {
    method: 'POST',
  });
  clearAdminSessionToken();
}

export async function getAdminSession(): Promise<AdminSession> {
  const session = await request<AdminSession>('/api/admin/session');
  if (!session.authenticated) {
    clearAdminSessionToken();
  }
  return session;
}

export async function listAdminCandidates(query: string = ''): Promise<AdminCandidateListResponse> {
  const params = new URLSearchParams();
  const trimmed = query.trim();
  if (trimmed) {
    params.set('q', trimmed);
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return request<AdminCandidateListResponse>(`/api/admin/candidates${suffix}`);
}

export async function getAdminCandidateDetail(candidateId: string): Promise<AdminCandidateDetail> {
  return request<AdminCandidateDetail>(`/api/admin/candidates/${candidateId}`);
}

export async function hireCandidate(
  candidateId: string,
  opts?: { rateOverride?: number; startDate?: string },
): Promise<HireResponse> {
  const params = new URLSearchParams();
  if (opts?.rateOverride !== undefined) params.set('rate_override', String(opts.rateOverride));
  if (opts?.startDate) params.set('start_date', opts.startDate);
  const qs = params.toString();
  return request<HireResponse>(`/api/admin/candidates/${candidateId}/hire${qs ? `?${qs}` : ''}`, {
    method: 'POST',
  });
}

export async function rejectCandidate(
  candidateId: string,
): Promise<{ candidate_id: string; rejected: boolean; ashby_stage_moved: boolean }> {
  return request(`/api/admin/candidates/${candidateId}/reject`, {
    method: 'POST',
  });
}

export async function computeRateProposal(
  candidateId: string,
  opts?: { force?: boolean },
): Promise<import('../types').RateProposal> {
  const qs = opts?.force ? '?force=true' : '';
  return request<import('../types').RateProposal>(
    `/api/admin/candidates/${candidateId}/compute-rate${qs}`,
    { method: 'POST' },
  );
}

export async function syncScoringConfigFromCode(): Promise<{
  synced: boolean;
  changed_fields: string[];
  new_version_id: string | null;
  version_label?: string;
  message: string;
}> {
  return request(`/api/admin/scoring-config/sync-from-code`, { method: 'POST' });
}

export async function overturnRejection(
  candidateId: string,
): Promise<{
  candidate_id: string;
  overturned: boolean;
  ashby_stage_moved: boolean;
  ashby_target_stage: string | null;
}> {
  return request(`/api/admin/candidates/${candidateId}/overturn-rejection`, {
    method: 'POST',
  });
}

export async function confirmHireCandidate(
  candidateId: string,
): Promise<{ candidate_id: string; hired: boolean; ashby_stage_moved: boolean }> {
  return request(`/api/admin/candidates/${candidateId}/confirm-hire`, {
    method: 'POST',
  });
}

export async function overrideCompositeScore(
  candidateId: string,
  stage: 1 | 2,
  reason: string,
): Promise<import('../types').CompositeScore> {
  return request<import('../types').CompositeScore>(
    `/api/admin/candidates/${candidateId}/composite-override`,
    {
      method: 'POST',
      body: JSON.stringify({ stage, reason }),
    },
  );
}

export async function createAdminNote(
  candidateId: string,
  content: string,
): Promise<import('../types').AdminNote> {
  return request(`/api/admin/candidates/${candidateId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function deleteAdminNote(
  candidateId: string,
  noteId: string,
): Promise<void> {
  return request(`/api/admin/candidates/${candidateId}/notes/${noteId}`, {
    method: 'DELETE',
  });
}

export type ResetTarget = 'resume' | 'stage_1' | 'stage_2' | 'stage_2_writing';

export interface ResetCandidateResponse {
  candidate_id: string;
  target: ResetTarget;
  new_current_step: string;
  reason: string;
  deleted: Record<string, number>;
  deleted_ids: Record<string, string[]>;
}

export async function resetCandidate(
  candidateId: string,
  target: ResetTarget,
  reason: string,
): Promise<ResetCandidateResponse> {
  return request<ResetCandidateResponse>(`/api/admin/candidates/${candidateId}/reset`, {
    method: 'POST',
    body: JSON.stringify({ target, reason }),
  });
}

export async function sendAdminEmail(
  candidateId: string,
  subject: string,
  body: string,
): Promise<{ ok: boolean; to: string }> {
  return request<{ ok: boolean; to: string }>(`/api/admin/candidates/${candidateId}/send-email`, {
    method: 'POST',
    body: JSON.stringify({ subject, body }),
  });
}

export interface RunScoringResult {
  ok: boolean;
  candidate_id: string;
  stage: number;
  interview_id: string;
  composite_id: string;
  composite_score: number;
  passed_threshold: boolean;
  requires_human_review: boolean;
  new_current_step: string | null;
}

export async function runStageScoring(
  candidateId: string,
  stage: 1 | 2,
  opts?: { interviewId?: string; reason?: string },
): Promise<RunScoringResult> {
  return request<RunScoringResult>(`/api/admin/candidates/${candidateId}/run-scoring/${stage}`, {
    method: 'POST',
    body: JSON.stringify({
      interview_id: opts?.interviewId ?? null,
      reason: opts?.reason ?? null,
    }),
  });
}

export interface RescoreResult {
  ok: boolean;
  composite_score: number;
  composite_score_raw: number;
  passed_threshold: boolean;
  dimension_scores: Record<string, { score: number; label: string; overridden: boolean }>;
  scores_deleted: number;
  composites_deleted: number;
}

export async function rescoreCandidate(
  candidateId: string,
  interviewId: string,
  dimensionOverrides: Record<string, number>,
  reason: string,
): Promise<RescoreResult> {
  return request<RescoreResult>(`/api/admin/candidates/${candidateId}/rescore`, {
    method: 'POST',
    body: JSON.stringify({
      interview_id: interviewId,
      dimension_overrides: dimensionOverrides,
      reason,
    }),
  });
}

export async function refreshInviteLink(candidateId: string): Promise<{ magic_link: string }> {
  return request<{ magic_link: string }>(`/api/admin/candidates/${candidateId}/refresh-invite`, {
    method: 'POST',
  });
}

// ─── Support Action Endpoints ────────────────────────────────────────────────

export async function listSupportActions(): Promise<import('../types').AdminSupportActionListResponse> {
  return request<import('../types').AdminSupportActionListResponse>('/api/admin/support/actions');
}

// ─── Scoring Config Endpoints ───────────────────────────────────────────────

import type {
  RescoreBatch,
  ScoringConfigVersion,
  ScoringConfigPayload,
  ScoringSimulation,
  ScoringSimulationDetail,
  SimulateScoringConfigResponse,
  ResumeFlagReview,
  ResumeWithFlags,
} from '../types';

export async function getActiveScoringConfig(): Promise<ScoringConfigVersion> {
  return request<ScoringConfigVersion>('/api/admin/scoring-config/active');
}

export async function listScoringConfigVersions(): Promise<ScoringConfigVersion[]> {
  return request<ScoringConfigVersion[]>('/api/admin/scoring-config/versions');
}

export async function createScoringConfigVersion(payload: {
  version_label: string;
  notes?: string | null;
  parent_version_id?: string | null;
  config: ScoringConfigPayload;
}): Promise<ScoringConfigVersion> {
  return request<ScoringConfigVersion>('/api/admin/scoring-config/versions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function activateScoringConfigVersion(
  versionId: string,
): Promise<ScoringConfigVersion> {
  return request<ScoringConfigVersion>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(versionId)}/activate`,
    { method: 'POST' },
  );
}

export interface ScoringConfigPrExportResponse {
  version_id: string;
  pr_url: string;
  branch: string;
  number: number;
}

export async function exportScoringConfigPr(
  versionId: string,
): Promise<ScoringConfigPrExportResponse> {
  return request<ScoringConfigPrExportResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(versionId)}/export-pr`,
    { method: 'POST' },
  );
}

export async function simulateScoringConfig(payload: {
  config: ScoringConfigPayload;
  stage: 1 | 2;
  window_days?: number;
  max_candidates?: number;
}): Promise<SimulateScoringConfigResponse> {
  return request<SimulateScoringConfigResponse>('/api/admin/scoring-config/simulate', {
    method: 'POST',
    body: JSON.stringify({
      window_days: 30,
      max_candidates: 50,
      ...payload,
    }),
  });
}

// ─── Rescore Batch Endpoints ────────────────────────────────────────────────

export async function applyRescoreBatch(payload: {
  scoring_config_id: string;
  window_days?: number;
  dry_run?: boolean;
}): Promise<RescoreBatch> {
  return request<RescoreBatch>('/api/admin/scoring-config/rescore-batches', {
    method: 'POST',
    body: JSON.stringify({
      window_days: 60,
      dry_run: false,
      ...payload,
    }),
  });
}

export async function listRescoreBatches(): Promise<RescoreBatch[]> {
  return request<RescoreBatch[]>('/api/admin/scoring-config/rescore-batches');
}

export async function revertRescoreBatch(batchId: string): Promise<RescoreBatch> {
  return request<RescoreBatch>(
    `/api/admin/scoring-config/rescore-batches/${encodeURIComponent(batchId)}/revert`,
    { method: 'POST' },
  );
}

// ─── Full-rescore simulation endpoints ──────────────────────────────────────
// Re-runs the LLM judge against transcripts so prompt + rubric edits are
// reflected. Background job — list/get for status + results.

export async function createScoringSimulation(payload: {
  label: string;
  notes?: string | null;
  config: ScoringConfigPayload;
  scoring_config_id?: string | null;
  stages?: number[];
  window_days?: number;
  max_candidates?: number;
}): Promise<ScoringSimulation> {
  return request<ScoringSimulation>('/api/admin/scoring-simulations', {
    method: 'POST',
    body: JSON.stringify({
      stages: [1, 2],
      window_days: 30,
      max_candidates: 10,
      ...payload,
    }),
  });
}

export async function listScoringSimulations(
  limit = 50,
): Promise<ScoringSimulation[]> {
  return request<ScoringSimulation[]>(
    `/api/admin/scoring-simulations?limit=${encodeURIComponent(String(limit))}`,
  );
}

export async function getScoringSimulation(
  simulationId: string,
): Promise<ScoringSimulationDetail> {
  return request<ScoringSimulationDetail>(
    `/api/admin/scoring-simulations/${encodeURIComponent(simulationId)}`,
  );
}

export async function cancelScoringSimulation(
  simulationId: string,
): Promise<ScoringSimulation> {
  return request<ScoringSimulation>(
    `/api/admin/scoring-simulations/${encodeURIComponent(simulationId)}/cancel`,
    { method: 'POST' },
  );
}

// ─── Resume Flag Review Endpoints ───────────────────────────────────────────

export async function listResumesWithFlags(opts?: {
  window_days?: number;
  only_flagged?: boolean;
  limit?: number;
}): Promise<ResumeWithFlags[]> {
  const params = new URLSearchParams();
  if (opts?.window_days !== undefined) params.set('window_days', String(opts.window_days));
  if (opts?.only_flagged !== undefined) params.set('only_flagged', String(opts.only_flagged));
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return request<ResumeWithFlags[]>(`/api/admin/resumes/with-flags${qs ? `?${qs}` : ''}`);
}

export async function createResumeFlagReview(
  resumeId: string,
  payload: {
    action: 'false_positive' | 'should_have_flagged' | 'confirmed';
    flag_text?: string | null;
    correct_text?: string | null;
    note?: string | null;
  },
): Promise<ResumeFlagReview> {
  return request<ResumeFlagReview>(
    `/api/admin/resumes/${encodeURIComponent(resumeId)}/flag-reviews`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function listResumeFlagReviews(
  untriagedOnly = false,
): Promise<ResumeFlagReview[]> {
  const qs = untriagedOnly ? '?untriaged_only=true' : '';
  return request<ResumeFlagReview[]>(`/api/admin/resumes/flag-reviews${qs}`);
}

// ─── Feedback Endpoints ──────────────────────────────────────────────────────

export interface FeedbackContext {
  page?: string;
  stage?: number | null;
  interview_id?: string | null;
  current_step?: string | null;
  user_agent?: string;
  viewport?: string;
  referrer?: string;
}

export async function submitFeedback(
  candidateId: string,
  comment: string,
  context: FeedbackContext = {},
): Promise<{ id: string; ok: boolean }> {
  return request<{ id: string; ok: boolean }>(`/api/candidates/${candidateId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ comment, ...context }),
  });
}

// ─── Diagnostic Event Reporting ──────────────────────────────────────────────

/**
 * Fire-and-forget client diagnostic event to backend for server-side logging.
 * Never throws — failures are silently ignored so interview flow is never blocked.
 */
export function reportClientEvent(
  interviewId: string,
  type: string,
  data?: Record<string, unknown>,
  options?: { keepalive?: boolean },
): void {
  const payload = { type, ...data };
  const authHeader: Record<string, string> = _sessionToken ? { 'Authorization': `Bearer ${_sessionToken}` } : {};
  fetch(`${BASE_URL}/api/interviews/${interviewId}/client-event`, {
    method: 'POST',
    keepalive: options?.keepalive ?? false,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
    },
    body: JSON.stringify(payload),
  }).catch(() => {}); // silent — never block the interview
}

// ─── Client transcript persistence ───────────────────────────────────────────
//
// This path is load-bearing.  Roughly 11% of production interviews record NOTHING
// from the agent worker — its whole HTTP path to the backend dies for the session
// while the voice conversation (LiveKit/Deepgram/OpenAI/Cartesia) works fine.  In
// those sessions the browser snapshot is the ONLY record of the interview, so a
// silently-dropped save costs a candidate their attempt.
//
// Three guarantees:
//  1. A save that fails is retried with backoff instead of being swallowed.
//  2. A save that still fails is spooled to localStorage and re-sent later —
//     including after a reload, from a different page, or on the next visit.
//  3. HTTP errors are treated as failures.  A bare `fetch` resolves on 401/500,
//     so the old `.catch()`-only handling reported success for rejected saves.

const TRANSCRIPT_SPOOL_KEY = 'baba:pending-client-transcripts';
const TRANSCRIPT_MAX_ATTEMPTS = 4;
const TRANSCRIPT_BACKOFF_MS = [0, 1000, 4000, 10000];

interface SpooledTranscript {
  interviewId: string;
  messages: ClientTranscriptMessage[];
  savedAt: number;
}

function readSpool(): SpooledTranscript[] {
  try {
    const raw = window.localStorage.getItem(TRANSCRIPT_SPOOL_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // storage disabled or corrupt — degrade to in-memory only
  }
}

function writeSpool(entries: SpooledTranscript[]): void {
  try {
    // Keep only the newest snapshot per interview — each one is a full replacement,
    // so older entries carry no information the newest lacks.
    const newest = new Map<string, SpooledTranscript>();
    for (const entry of entries) {
      const prior = newest.get(entry.interviewId);
      if (!prior || entry.savedAt >= prior.savedAt) newest.set(entry.interviewId, entry);
    }
    window.localStorage.setItem(TRANSCRIPT_SPOOL_KEY, JSON.stringify([...newest.values()]));
  } catch {
    // Quota exceeded or storage disabled — nothing useful to do here.
  }
}

function spoolTranscript(interviewId: string, messages: ClientTranscriptMessage[]): void {
  writeSpool([...readSpool(), { interviewId, messages, savedAt: Date.now() }]);
}

function unspoolTranscript(interviewId: string, savedAt: number): void {
  // Only drop the spooled entry if nothing newer replaced it while we were sending.
  writeSpool(readSpool().filter(e => !(e.interviewId === interviewId && e.savedAt <= savedAt)));
}

async function postClientTranscript(
  interviewId: string,
  messages: ClientTranscriptMessage[],
  keepalive: boolean,
): Promise<boolean> {
  const authHeader: Record<string, string> = _sessionToken ? { 'Authorization': `Bearer ${_sessionToken}` } : {};
  const response = await fetch(`${BASE_URL}/api/interviews/${interviewId}/client-transcript`, {
    method: 'POST',
    keepalive,
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({ messages }),
  });
  // 4xx other than 408/429 will not succeed on retry (bad session, wrong candidate,
  // deleted interview).  Report it as terminal so we stop burning attempts, but keep
  // the snapshot spooled — a later visit re-authenticates and can still deliver it.
  if (!response.ok && response.status >= 400 && response.status < 500
      && response.status !== 408 && response.status !== 429) {
    throw new Error(`client-transcript rejected: ${response.status}`);
  }
  if (!response.ok) throw new Error(`client-transcript failed: ${response.status}`);
  return true;
}

/**
 * Persist a browser transcript snapshot so an interview remains recoverable even if
 * the agent process never flushes its transcript.
 *
 * Retries with backoff, then spools to localStorage so the snapshot survives a
 * reload and is redelivered by `flushPendingClientTranscripts`.  Never throws and
 * never blocks the interview.
 */
export function saveClientTranscript(
  interviewId: string,
  messages: ClientTranscriptMessage[],
  options?: { keepalive?: boolean },
): void {
  if (!messages.length) return;
  const keepalive = options?.keepalive ?? false;

  // On a keepalive save the page is going away, so there is no time to retry.
  // Spool first, fire once, and let the next page load drain whatever did not land.
  if (keepalive) {
    const savedAt = Date.now();
    spoolTranscript(interviewId, messages);
    postClientTranscript(interviewId, messages, true)
      .then(() => unspoolTranscript(interviewId, savedAt))
      .catch(() => {});
    return;
  }

  void (async () => {
    const savedAt = Date.now();
    for (let attempt = 0; attempt < TRANSCRIPT_MAX_ATTEMPTS; attempt++) {
      if (TRANSCRIPT_BACKOFF_MS[attempt] > 0) {
        await new Promise(r => setTimeout(r, TRANSCRIPT_BACKOFF_MS[attempt]));
      }
      try {
        await postClientTranscript(interviewId, messages, false);
        unspoolTranscript(interviewId, savedAt);
        return;
      } catch {
        // Spool on the first failure so a hard tab close mid-retry still keeps it.
        if (attempt === 0) spoolTranscript(interviewId, messages);
      }
    }
  })();
}

/** Report that Stage 1 scoring did not produce a result. */
export function reportScoringFailure(
  candidateId: string | undefined,
  kind: 'timeout' | 'error',
): void {
  if (!candidateId) return;
  const authHeader: Record<string, string> = _sessionToken ? { 'Authorization': `Bearer ${_sessionToken}` } : {};
  fetch(`${BASE_URL}/api/candidates/${candidateId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({
      comment:
        `[AUTOMATED] Stage 1 scoring produced no result (${kind}). The candidate finished ` +
        `their interview and is stranded on /interview-complete — they have no composite ` +
        `score, so Begin Case Simulation will return 403. Needs an ops fix: score the ` +
        `existing transcript if one survived, otherwise reset or override. ` +
        `The candidate has been told we hit a problem and not to redo the interview.`,
      page: '/interview-complete',
      stage: 1,
      current_step: '/interview-complete',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : undefined,
    }),
  }).catch(() => {});
}

/**
 * Re-send any transcript snapshots that never reached the backend.
 *
 * Safe to call at any time — the endpoint is idempotent (each snapshot is a full
 * replacement of the stored transcript) and entries are dropped only once delivered.
 */
export function flushPendingClientTranscripts(): void {
  const pending = readSpool();
  if (!pending.length) return;
  for (const entry of pending) {
    postClientTranscript(entry.interviewId, entry.messages, false)
      .then(() => unspoolTranscript(entry.interviewId, entry.savedAt))
      .catch(() => {}); // still pending — a later flush will retry
  }
}

// ─── Re-export error class ────────────────────────────────────────────────────

export { ApiError };
